
/**
 * Validates if a string can be parsed into a valid date
 * @param dateString The date string to validate
 * @returns boolean indicating if the date is valid
 */
export const isValidDateString = (dateString: string | null | undefined): boolean => {
  if (!dateString) return false;
  
  try {
    // Handle special case of "ASAP" or similar text values
    if (typeof dateString === 'string' && 
        (/asap/i.test(dateString) || 
         /no deadline/i.test(dateString) || 
         /unknown/i.test(dateString))) {
      return false;
    }
    
    // Handle TED API date format where dates can come as arrays
    if (Array.isArray(dateString) && dateString.length > 0) {
      return isValidDateString(dateString[0]);
    }
    
    // Handle dates with timezone offsets like "2025-02-11+01:00"
    let processedDateString = dateString;
    
    // Handle dates with timezone offsets without 'T'
    if (typeof processedDateString === 'string' && processedDateString.includes('+') && !processedDateString.includes('T')) {
      // Convert to ISO format by adding time
      const parts = processedDateString.split('+');
      processedDateString = `${parts[0]}T00:00:00+${parts[1]}`;
    }
    
    // Handle Zulu time format without proper 'T'
    if (typeof processedDateString === 'string' && processedDateString.includes('Z') && !processedDateString.includes('T')) {
      processedDateString = processedDateString.replace('Z', 'T00:00:00Z');
    }
    
    // Handle YYYYMMDD format
    if (typeof processedDateString === 'string' && /^\d{8}$/.test(processedDateString)) {
      const year = processedDateString.substring(0, 4);
      const month = processedDateString.substring(4, 6);
      const day = processedDateString.substring(6, 8);
      processedDateString = `${year}-${month}-${day}`;
    }
    
    // Handle DD.MM.YYYY format (common in EU countries)
    if (typeof processedDateString === 'string' && /^\d{2}\.\d{2}\.\d{4}$/.test(processedDateString)) {
      const parts = processedDateString.split('.');
      processedDateString = `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    
    // Handle DD/MM/YYYY format
    if (typeof processedDateString === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(processedDateString)) {
      const parts = processedDateString.split('/');
      processedDateString = `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    
    // Handle invalid date strings that may be passed
    if (typeof processedDateString === 'string' && 
        (processedDateString === "Invalid Date" || 
         processedDateString === "NaN" || 
         processedDateString.length < 5)) {
      return false;
    }
    
    const date = new Date(processedDateString);
    return !isNaN(date.getTime());
  } catch (e) {
    console.error("Error validating date:", dateString, e);
    return false;
  }
};

/**
 * Safely converts a date string to a Date object
 * @param dateString The date string to convert
 * @returns Date object or null if invalid
 */
export const safeParseDate = (dateString: string | null | undefined): Date | null => {
  if (!dateString) return null;
  
  try {
    // Handle special case of "ASAP" or similar text values
    if (typeof dateString === 'string' && 
        (/asap/i.test(dateString) || 
         /no deadline/i.test(dateString) || 
         /unknown/i.test(dateString))) {
      return null;
    }
    
    // Handle TED API date format where dates can come as arrays
    if (Array.isArray(dateString) && dateString.length > 0) {
      return safeParseDate(dateString[0]);
    }
    
    // Handle dates with timezone offsets like "2025-02-11+01:00"
    let processedDateString = dateString;
    
    // Handle dates with timezone offsets without 'T'
    if (typeof processedDateString === 'string' && processedDateString.includes('+') && !processedDateString.includes('T')) {
      // Convert to ISO format by adding time
      const parts = processedDateString.split('+');
      processedDateString = `${parts[0]}T00:00:00+${parts[1]}`;
    }
    
    // Handle Zulu time format without proper 'T'
    if (typeof processedDateString === 'string' && processedDateString.includes('Z') && !processedDateString.includes('T')) {
      processedDateString = processedDateString.replace('Z', 'T00:00:00Z');
    }
    
    // Handle YYYYMMDD format
    if (typeof processedDateString === 'string' && /^\d{8}$/.test(processedDateString)) {
      const year = processedDateString.substring(0, 4);
      const month = processedDateString.substring(4, 6);
      const day = processedDateString.substring(6, 8);
      processedDateString = `${year}-${month}-${day}`;
    }
    
    // Handle DD.MM.YYYY format (common in EU countries)
    if (typeof processedDateString === 'string' && /^\d{2}\.\d{2}\.\d{4}$/.test(processedDateString)) {
      const parts = processedDateString.split('.');
      processedDateString = `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    
    // Handle DD/MM/YYYY format
    if (typeof processedDateString === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(processedDateString)) {
      const parts = processedDateString.split('/');
      processedDateString = `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    
    // Handle invalid date strings that may be passed
    if (typeof processedDateString === 'string' && 
        (processedDateString === "Invalid Date" || 
         processedDateString === "NaN" || 
         processedDateString.length < 5)) {
      return null;
    }
    
    const date = new Date(processedDateString);
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date: ${dateString} (processed as: ${processedDateString})`);
      return null;
    }
    
    return date;
  } catch (e) {
    console.error("Error parsing date:", dateString, e);
    return null;
  }
};

/**
 * Safely formats a date string using a formatter function
 * @param dateString The date string to format
 * @param formatter Function to format the date
 * @param fallbackValue Value to return if date is invalid
 * @returns Formatted date string or fallback value
 */
export const safeFormatDate = (
  dateString: string | null | undefined, 
  formatter: (date: Date) => string,
  fallbackValue: string = "Invalid date"
): string => {
  const date = safeParseDate(dateString);
  if (!date) return fallbackValue;
  
  try {
    return formatter(date);
  } catch (e) {
    console.error("Error formatting date:", dateString, e);
    return fallbackValue;
  }
};
